import { SearchableKeyword } from '../../../../../../ui/gzSearchableAccordionsPanel/types';
import { translateFn } from '../../../../../../utils/translator.helper';
import {
  FirewallSettingsSection,
  FirewallIdsProfileType,
  FirewallLogVerbosityLevel,
  FirewallNetworkSection,
  FirewallNetworkType,
  FirewallNetworkIdentification,
  FirewallAdapterType,
  FirewallAdapterNetworkType,
  FirewallAdapterStealthMode,
  FirewallAdvancedSection,
  FirewallAdvancedProtectionLevel,
  FirewallAdvancedRuleType,
  FirewallAdvancedRulePermissionType,
  FirewallAdvancedRuleProtocolType,
  FirewallAdvancedRuleCustomProtocolType,
  FirewallAdvancedRuleSettingsDirection,
} from '@nirvana/shared/dtos/types/policies/common';
import {
  FirewallAdvancedNetworkFrontend,
  FirewallAdvancedRuleProtocolTypeForFrontend,
  FirewallAdvancedSearchableKeywords,
  FirewallNetworkSearchableKeywords,
  FirewallSettingsSearchableKeywords,
} from './firewall.types';
import { SelectOption } from '../../../../../../ui/gzDropdown/types';
import { IColumnOption } from '../../../../../../ui/gzDataTable/types';
import { ColumnValue } from '../../../../../../grid/types/common';
import { ListItem } from '../../../../../../ui/gzSingleSelectList/types';

export const FIREWALL_SETTINGS_DEFAULT_VALUE: FirewallSettingsSection = {
  enable: true,
  blockPortScan: true,
  allowInternetConnectionSharing: false,
  monitorWiFiConnections: false,
  logVerbosity: {
    enable: true,
    level: FirewallLogVerbosityLevel.LOW,
  },
  instructionsDetectionSystem: {
    enable: false,
    profile: FirewallIdsProfileType.NORMAL,
  },
  exclusions: {
    enable: false,
    duplicateToNetworkProtection: true,
    items: [],
  },
};

export const FIREWALL_NETWORK_DEFAULT_VALUE: FirewallNetworkSection = {
  adapters: [],
};

export const FIREWALL_ADVANCED_DEFAULT_VALUE: FirewallAdvancedSection = {
  protectionLevel: FirewallAdvancedProtectionLevel.RULESET_KNOWN_FILES_AND_ALLOW,
  createAgresiveRules: false,
  createRulesForAppsBlockedByIDS: true,
  monitorProcessChanges: true,
  ignoreSignedProcesses: true,
  rule: [],
};

export const DEFAULT_MAC_ADDRESS = '00-00-00-00-00-00';

export function getLogVerbosityLevelOptions(): Array<SelectOption> {
  return [
    {
      title: translateFn('Low'),
      value: FirewallLogVerbosityLevel.LOW,
    },
    {
      title: translateFn('Medium'),
      value: FirewallLogVerbosityLevel.MEDIUM,
    },
    {
      title: translateFn('High'),
      value: FirewallLogVerbosityLevel.HIGH,
    },
  ];
}

// network type options
function getLabelByNetworkType(type: FirewallNetworkType | FirewallAdapterNetworkType): string {
  switch (type) {
    case FirewallNetworkType.TRUSTED:
      return translateFn('Trusted');
    case FirewallNetworkType.HOME_OFFICE:
      return translateFn('Home/Office');
    case FirewallNetworkType.PUBLIC:
      return translateFn('Public');
    case FirewallNetworkType.UNTRUSTED:
      return translateFn('Untrusted');
    case FirewallAdapterNetworkType.LET_WINDOWS_DECIDE:
      return translateFn('Let Windows decide');
    default:
      return '';
  }
}
export function getNetworkTypeFormOptions(): IColumnOption[] | ListItem[] {
  return [
    {
      label: getLabelByNetworkType(FirewallNetworkType.TRUSTED),
      value: FirewallNetworkType.TRUSTED,
    },
    {
      label: getLabelByNetworkType(FirewallNetworkType.HOME_OFFICE),
      value: FirewallNetworkType.HOME_OFFICE,
    },
    {
      label: getLabelByNetworkType(FirewallNetworkType.PUBLIC),
      value: FirewallNetworkType.PUBLIC,
    },
    {
      label: getLabelByNetworkType(FirewallNetworkType.UNTRUSTED),
      value: FirewallNetworkType.UNTRUSTED,
    },
  ];
}
export function getAdapterNetworkTypeFormOptionsForGrid(): IColumnOption[] {
  return [
    {
      label: getLabelByNetworkType(FirewallAdapterNetworkType.TRUSTED),
      value: FirewallAdapterNetworkType.TRUSTED,
    },
    {
      label: getLabelByNetworkType(FirewallAdapterNetworkType.HOME_OFFICE),
      value: FirewallAdapterNetworkType.HOME_OFFICE,
    },
    {
      label: getLabelByNetworkType(FirewallAdapterNetworkType.PUBLIC),
      value: FirewallAdapterNetworkType.PUBLIC,
    },
    {
      label: getLabelByNetworkType(FirewallAdapterNetworkType.UNTRUSTED),
      value: FirewallAdapterNetworkType.UNTRUSTED,
    },
    {
      label: getLabelByNetworkType(FirewallAdapterNetworkType.LET_WINDOWS_DECIDE),
      value: FirewallAdapterNetworkType.LET_WINDOWS_DECIDE,
    },
  ];
}

// adapter stealth mode options
export function getAdapterStealthModeFormOptionsForGrid(): IColumnOption[] {
  return [
    {
      label: translateFn('Yes'),
      value: FirewallAdapterStealthMode.OFF,
    },
    {
      label: translateFn('No'),
      value: FirewallAdapterStealthMode.ON,
    },
    {
      label: translateFn('Remote'),
      value: FirewallAdapterStealthMode.REMOTE,
    },
  ];
}

// network identification options
function getLabelByNetworkIdentification(type: FirewallNetworkIdentification): string {
  switch (type) {
    case FirewallNetworkIdentification.DNS:
      return translateFn('DNS');
    case FirewallNetworkIdentification.GATEWAY:
      return translateFn('Gateway');
    case FirewallNetworkIdentification.NETWORK:
      return translateFn('Network');
    default:
      return '';
  }
}
export function getNetworkIdentification(): IColumnOption[] | ListItem[] {
  return [
    {
      label: getLabelByNetworkIdentification(FirewallNetworkIdentification.DNS),
      value: FirewallNetworkIdentification.DNS,
    },
    {
      label: getLabelByNetworkIdentification(FirewallNetworkIdentification.GATEWAY),
      value: FirewallNetworkIdentification.GATEWAY,
    },
    {
      label: getLabelByNetworkIdentification(FirewallNetworkIdentification.NETWORK),
      value: FirewallNetworkIdentification.NETWORK,
    },
  ];
}

// adapter type options
export function getAdapterTypeFormGrid(): IColumnOption[] {
  return [
    {
      label: translateFn('Wired'),
      value: FirewallAdapterType.WIRED,
    },
    {
      label: translateFn('Wireless'),
      value: FirewallAdapterType.WIRELESS,
    },
    {
      label: translateFn('Virtual'),
      value: FirewallAdapterType.VIRTUAL,
    },
  ];
}

// advanced protection level
export function getAdvancedProtectionLevelOptionsForDropdown(): SelectOption[] {
  return [
    {
      title: translateFn('Ruleset and allow'),
      value: FirewallAdvancedProtectionLevel.RULESET_AND_ALLOW,
    },
    {
      title: translateFn('Ruleset and ask'),
      value: FirewallAdvancedProtectionLevel.RULESET_AND_ASK,
    },
    {
      title: translateFn('Ruleset and deny'),
      value: FirewallAdvancedProtectionLevel.RULESET_AND_DENY,
    },
    {
      title: translateFn('Ruleset, known files and allow'),
      value: FirewallAdvancedProtectionLevel.RULESET_KNOWN_FILES_AND_ALLOW,
    },
    {
      title: translateFn('Ruleset, known files and ask'),
      value: FirewallAdvancedProtectionLevel.RULESET_KNOWN_FILES_AND_ASK,
    },
    {
      title: translateFn('Ruleset, known files and deny'),
      value: FirewallAdvancedProtectionLevel.RULESET_KNOWN_FILES_AND_DENY,
    },
  ];
}

export function getAdvancedRuleStatusOptionsForGrid(): ColumnValue[] {
  return [
    {
      label: translateFn('Enabled'),
      value: 1,
    },
    {
      label: translateFn('Disabled'),
      value: 0,
    },
  ];
}

// advanced rule type
export function getAdvancedRuleTypeOptionsForGrid(): ColumnValue[] {
  return [
    {
      label: translateFn('Application'),
      value: FirewallAdvancedRuleType.APPLICATION,
    },
    {
      label: translateFn('Connection'),
      value: FirewallAdvancedRuleType.CONNECTION,
    },
  ];
}

// advanced permission
export function getAdvancedNetworkOptionsForGrid(): ColumnValue[] {
  return [
    {
      label: translateFn('Home/Office'),
      value: FirewallAdvancedNetworkFrontend.HOME,
    },
    {
      label: translateFn('Public'),
      value: FirewallAdvancedNetworkFrontend.PUBLIC,
    },
  ];
}

export function getAdvancedPermissionTypeOptionsForGrid(): ColumnValue[] {
  return [
    {
      label: translateFn('Allow'),
      value: FirewallAdvancedRulePermissionType.ALLOW,
    },
    {
      label: translateFn('Deny'),
      value: FirewallAdvancedRulePermissionType.DENY,
    },
  ];
}

// advanced protocol options
export function getConvertedProtocolTypeForFrontend(
  type: FirewallAdvancedRuleProtocolType
): FirewallAdvancedRuleProtocolTypeForFrontend {
  switch (type) {
    case FirewallAdvancedRuleProtocolType.ANY:
      return FirewallAdvancedRuleProtocolTypeForFrontend.ANY;
    case FirewallAdvancedRuleProtocolType.TCP:
      return FirewallAdvancedRuleProtocolTypeForFrontend.TCP;
    case FirewallAdvancedRuleProtocolType.UDP:
      return FirewallAdvancedRuleProtocolTypeForFrontend.UDP;
    default:
      return FirewallAdvancedRuleProtocolTypeForFrontend.CUSTOM;
  }
}
export function getAdvancedProtocolOptionsForGrid(): ColumnValue[] {
  const advancedProtocolOptions: {
    label: string;
    value: FirewallAdvancedRuleProtocolTypeForFrontend | FirewallAdvancedRuleCustomProtocolType;
  }[] = [
    {
      label: translateFn('Any'),
      value: FirewallAdvancedRuleProtocolTypeForFrontend.ANY,
    },
    {
      label: translateFn('TCP'),
      value: FirewallAdvancedRuleProtocolTypeForFrontend.TCP,
    },
    {
      label: translateFn('UDP'),
      value: FirewallAdvancedRuleProtocolTypeForFrontend.UDP,
    },
  ];
  for (const [key, value] of Object.entries(FirewallAdvancedRuleCustomProtocolType)) {
    advancedProtocolOptions.push({
      label: key,
      value: value,
    });
  }

  return advancedProtocolOptions;
}

export function getAdvancedProtocolOptionsForDropdown(): SelectOption[] {
  const advancedProtocolOptions: {
    title: string;
    value: FirewallAdvancedRuleCustomProtocolType;
  }[] = [];

  for (const [key, value] of Object.entries(FirewallAdvancedRuleCustomProtocolType)) {
    advancedProtocolOptions.push({
      title: key,
      value: value,
    });
  }

  return advancedProtocolOptions;
}

export function getAdvancedDirectionOptionsForGrid(): ColumnValue[] {
  return [
    {
      label: translateFn('Both'),
      value: FirewallAdvancedRuleSettingsDirection.BOTH,
    },
    {
      label: translateFn('Inbound'),
      value: FirewallAdvancedRuleSettingsDirection.INBOUND,
    },
    {
      label: translateFn('Outbound'),
      value: FirewallAdvancedRuleSettingsDirection.OUTBOUND,
    },
  ];
}

// searchable keywords
export function getFirewallSettingsSearchableKeywords(): {
  [type in FirewallSettingsSearchableKeywords]: SearchableKeyword;
} {
  return {
    [FirewallSettingsSearchableKeywords.Firewall]: {
      translatedLabel: translateFn('Firewall'),
      label: 'Firewall',
    },
    [FirewallSettingsSearchableKeywords.Ics]: {
      translatedLabel: translateFn('Allow Internet Connection Sharing (ICS)'),
      label: 'Allow Internet Connection Sharing (ICS)',
    },
    [FirewallSettingsSearchableKeywords.MonitorWifi]: {
      translatedLabel: translateFn('Monitor Wi-Fi connections'),
      label: 'Monitor Wi-Fi connections',
    },
    [FirewallSettingsSearchableKeywords.Log]: {
      translatedLabel: translateFn('Log verbosity level'),
      label: 'Log verbosity level',
    },
    [FirewallSettingsSearchableKeywords.BlockScan]: {
      translatedLabel: translateFn('Block port scans'),
      label: 'Block port scans',
    },
    [FirewallSettingsSearchableKeywords.Exclusions]: {
      translatedLabel: translateFn('Exclusions'),
      label: 'Exclusions',
    },
    [FirewallSettingsSearchableKeywords.Duplicate]: {
      translatedLabel: translateFn('Duplicate to Network Protection'),
      label: 'Duplicate to Network Protection',
    },
    [FirewallSettingsSearchableKeywords.Ids]: {
      translatedLabel: translateFn('Intrusion Detection System (IDS)'),
      label: 'Intrusion Detection System (IDS)',
    },
    [FirewallSettingsSearchableKeywords.Aggressive]: {
      translatedLabel: translateFn('Aggressive'),
      label: 'Aggressive',
    },
    [FirewallSettingsSearchableKeywords.Normal]: {
      translatedLabel: translateFn('Normal'),
      label: 'Normal',
    },
    [FirewallSettingsSearchableKeywords.Permissive]: {
      translatedLabel: translateFn('Permissive'),
      label: 'Permissive',
    },
  };
}

export function getFirewallNetworkSearchableKeywords(): {
  [type in FirewallNetworkSearchableKeywords]: SearchableKeyword;
} {
  return {
    [FirewallNetworkSearchableKeywords.Settings]: {
      translatedLabel: translateFn('Settings'),
      label: 'Settings',
    },
    [FirewallNetworkSearchableKeywords.Networks]: {
      translatedLabel: translateFn('Networks'),
      label: 'Networks',
    },
    [FirewallNetworkSearchableKeywords.NetworkProfile]: {
      translatedLabel: translateFn('Network Profile'),
      label: 'Network Profile',
    },
    [FirewallNetworkSearchableKeywords.Mac]: {
      translatedLabel: translateFn('MAC'),
      label: 'MAC',
    },
    [FirewallNetworkSearchableKeywords.Ip]: {
      translatedLabel: translateFn('IP'),
      label: 'IP',
    },
    [FirewallNetworkSearchableKeywords.Adapters]: {
      translatedLabel: translateFn('Adapters'),
      label: 'Adapters',
    },
    [FirewallNetworkSearchableKeywords.Wired]: {
      translatedLabel: translateFn('Wired'),
      label: 'Wired',
    },
    [FirewallNetworkSearchableKeywords.Wireless]: {
      translatedLabel: translateFn('Wireless'),
      label: 'Wireless',
    },
    [FirewallNetworkSearchableKeywords.Virtual]: {
      translatedLabel: translateFn('Virtual'),
      label: 'Virtual',
    },
  };
}

export function getFirewallAdvancedSearchableKeywords(): {
  [type in FirewallAdvancedSearchableKeywords]: SearchableKeyword;
} {
  return {
    [FirewallAdvancedSearchableKeywords.Rules]: {
      translatedLabel: translateFn('Rules'),
      label: 'Rules',
    },
    [FirewallAdvancedSearchableKeywords.Settings]: {
      translatedLabel: translateFn('Settings'),
      label: 'Settings',
    },
    [FirewallAdvancedSearchableKeywords.ProtectionLevel]: {
      translatedLabel: translateFn('Protection level'),
      label: 'Protection level',
    },
    [FirewallAdvancedSearchableKeywords.CreateAggressiveRules]: {
      translatedLabel: translateFn('Create aggressive rules'),
      label: 'Create aggressive rules',
    },
    [FirewallAdvancedSearchableKeywords.CreateRules]: {
      translatedLabel: translateFn('Create rules for applications blocked by IDS'),
      label: 'Create rules for applications blocked by IDS',
    },
    [FirewallAdvancedSearchableKeywords.Monitor]: {
      translatedLabel: translateFn('Monitor process changes'),
      label: 'Monitor process changes',
    },
    [FirewallAdvancedSearchableKeywords.Ignore]: {
      translatedLabel: translateFn('Ignore signed processes'),
      label: 'Ignore signed processes',
    },
  };
}
